import { displayMessage } from '@/helpers/helper'
import { i18n } from '@/i18n'
import axios from 'axios'

/**
 * @typedef {Object} ReturnSettings
 * @property {string} [country_code] - Country code for the settings
 * @property {boolean} [default_settings] - Whether these are default settings
 * @property {Array<string>} [allow_return_creation_in_shop] - Array of roles allowed to create returns in shop
 * @property {Object} [pickup_hours] - Pickup hours configuration
 * @property {string} [pickup_hours.begin] - Start time for pickup
 * @property {string} [pickup_hours.end] - End time for pickup
 * @property {boolean} [available_address_picking] - Whether address picking is available
 * @property {Object} [charge_return_fees] - Return fees configuration
 * @property {number} [charge_return_fees.return_fees_amount] - Amount of return fees
 * @property {Object} [charge_return_fees.return_fees_currency] - Currency object for return fees
 * @property {boolean} [charge_return_fees.available] - Whether return fees are available
 * @property {boolean} [charge_return_fees.display_button] - Whether to display return fees button
 * @property {boolean} [charge_return_fees.prepaid_return_label] - Whether to generate prepaid return label
 * @property {Object} [refund_on_terminal] - Terminal refund configuration
 * @property {boolean} [refund_on_terminal.activate] - Whether terminal refund is activated
 * @property {boolean} [refund_on_terminal.activate_test_mode] - Whether test mode is activated
 * @property {boolean} [refund_on_terminal.send_return_reference] - Whether to send return reference
 * @property {Object} [display] - Display settings
 * @property {boolean} [display.return_label_button] - Whether to display return label button
 * @property {boolean} [display.item_custom_return_reason] - Whether to display custom return reason
 * @property {Array<string>} [display.item_return_reasons] - Array of item return reasons
 * @property {Array<string>} [display.public_item_return_reasons] - Array of public item return reasons
 * @property {Array<string>} [display.return_cancel_reasons] - Array of return cancel reasons
 * @property {Array<string>} [display.item_non_refund_reasons] - Array of non-refund reasons
 * @property {Array<string>} [reasons_requiring_validation] - Array of reasons requiring validation
 * @property {Object} [refund_order_carrier_cost] - Carrier cost refund configuration
 * @property {boolean} [refund_order_carrier_cost.activate] - Whether carrier cost refund is activated
 * @property {boolean} [refund_order_carrier_cost.display_refund_button] - Whether to display refund button
 * @property {Object} [waiting_products_receipt_timeout] - Timeout configuration for waiting products receipt
 * @property {number} [waiting_products_receipt_timeout.delay] - Delay in days
 * @property {boolean} [waiting_products_receipt_timeout.activate] - Whether timeout is activated
 * @property {Object} [block_return_creation] - Block return creation configuration
 * @property {number} [block_return_creation.delay] - Delay value
 * @property {string} [block_return_creation.duration] - Duration type (days, month)
 * @property {Object} [block_return_access] - Block return access configuration
 * @property {number} [block_return_access.delay] - Delay value
 * @property {string} [block_return_access.duration] - Duration type (days, month)
 * @property {Array<string>} [refund_creation_authorized_roles] - Array of roles authorized for refund creation
 */

/**
 * @typedef {Object} ReturnSettingsResponse
 * @property {Array<ReturnSettings>} data - Array of return settings
 * @property {number} [total] - Total number of settings
 */

/**
 * @typedef {Object} AuthProvider
 * @property {string} accessToken - The bearer token used for API authentication
 * @property {number} expiresAt - Token expiration timestamp in milliseconds
 * @property {Function} isAuthenticated - Method to refresh authentication status
 */

/**
 * Service for interacting with the Return Settings API.
 * Provides methods for return settings retrieval and management.
 *
 * @class ReturnSettingsService
 * @example
 * const returnSettingsService = new ReturnSettingsService(authProvider);
 * const settings = await returnSettingsService.getReturnSettings('FRA');
 */
export class ReturnSettingsService {
  /**
   * Authentication provider instance
   * @type {AuthProvider}
   */
  auth

  /**
   * Current access token for API requests
   * @type {string}
   */
  accessToken

  /**
   * Promise tracking ongoing token refresh operation
   * @type {Promise<void>|null}
   */
  tokenRefreshing

  /**
   * Axios instance configured for return settings API requests
   * @type {import('axios').AxiosInstance}
   */
  returnSettingsAPI

  /**
   * Creates a new instance of the ReturnSettingsService.
   *
   * Initializes the service with the provided authentication provider.
   * Sets up an Axios instance with request/response interceptors for automatic token refresh
   * and error handling.
   *
   * @constructor
   * @param {AuthProvider} auth - The authentication provider containing the access token
   * @throws {Error} Throws an error if the auth is invalid
   *
   * @example
   * const authProvider = {
   *   accessToken: 'bearer_token',
   *   expiresAt: Date.now() + 3600000,
   *   isAuthenticated: async () => { ... }
   * };
   * const service = new ReturnSettingsService(authProvider);
   */
  constructor(auth) {
    this.auth = auth
    this.accessToken = auth?.accessToken
    this.tokenRefreshing = null

    if (!auth?.accessToken) {
      this.emitError('missing_auth', true)
    }

    // Initialize the Axios HTTP client with interceptors
    this.returnSettingsAPI = axios.create({
      baseURL: process.env.VUE_APP_RETURN_API_URL,
      timeout: 29900,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${this.accessToken}`,
      },
    })

    // Request interceptor for automatic token refresh
    this.returnSettingsAPI.interceptors.request.use(
      (config) => this.refreshTokenIfNeeded(config),
      (error) => Promise.reject(error)
    )

    // Response interceptor for centralized error handling
    this.returnSettingsAPI.interceptors.response.use(
      (response) => response,
      (error) => {
        this.emitError(error?.response?.data?.message || 'api_error')
        console.error('Return Settings API:', error)
        return Promise.reject(error)
      }
    )
  }

  /**
   * Refreshes the access token if it's expired or about to expire before making API requests.
   * Prevents concurrent token refresh operations by using a shared promise.
   *
   * @private
   * @param {import('axios').AxiosRequestConfig} config - Axios request configuration object
   * @returns {Promise<import('axios').AxiosRequestConfig>} Updated configuration with fresh token
   */
  async refreshTokenIfNeeded(config) {
    const now = Date.now()

    // Check if token is still valid (with 30 second buffer before token expires)
    if (this.auth.expiresAt - 30000 > now) {
      return config
    }

    // Prevent concurrent token refreshes
    if (!this.tokenRefreshing) {
      this.tokenRefreshing = this.refreshToken()
    }

    await this.tokenRefreshing
    config.headers.Authorization = `Bearer ${this.accessToken}`
    return config
  }

  /**
   * Refreshes the authentication token by calling the auth provider's authentication method.
   * Updates the access token and Axios default headers with the new token.
   *
   * @private
   * @returns {Promise<void>} Promise that resolves when token refresh is complete
   * @throws {Error} Throws if token refresh fails
   */
  async refreshToken() {
    try {
      await this.auth.isAuthenticated()
      this.accessToken = this.auth.accessToken
      this.returnSettingsAPI.defaults.headers.Authorization = `Bearer ${this.accessToken}`
    } finally {
      this.tokenRefreshing = null
    }
  }

  /**
   * Displays error messages through the application's message system and optionally throws an error.
   * Uses internationalization for localized error messages.
   *
   * @private
   * @param {string} messageKey - Translation key for the error message in the return.settings namespace
   * @param {boolean} [withThrow=false] - Whether to throw an Error after displaying the message
   * @throws {Error} Throws an error with the localized message if withThrow is true
   */
  emitError(messageKey, withThrow = false) {
    const text = i18n.te(`return.settings.${messageKey}`)
      ? i18n.t(`return.settings.${messageKey}`)
      : messageKey
    displayMessage(text)
    if (withThrow) {
      throw new Error(text)
    }
  }

  /**
   * Makes a GET request to the return settings API with automatic parameter cleaning.
   * Removes null and undefined parameters before sending the request.
   *
   * @private
   * @param {string} path - API endpoint path relative to the base URL
   * @param {Object} [params={}] - Query parameters object
   * @returns {Promise<any>} Promise resolving to the API response data
   * @throws {Error} Throws if the API request fails
   */
  async get(path, params = {}) {
    // Remove undefined/null parameters
    const cleanedParams = Object.fromEntries(
      // eslint-disable-next-line no-unused-vars
      Object.entries(params).filter(([_key, value]) => ![null, undefined].includes(value))
    )

    const response = await this.returnSettingsAPI.get(path, { params: cleanedParams })
    return response.data
  }

  /**
   * Retrieves return settings for a specific country or default settings.
   * Searches for country-specific settings first, falls back to default settings if not found.
   *
   * @param {string} [countryCode] - Country code to filter settings (e.g., 'FRA', 'USA')
   * @returns {Promise<ReturnSettings|null>} Promise resolving to return settings or null if not found
   * @throws {Error} Throws if the API request fails
   *
   * @example
   * // Get settings for a specific country
   * const settings = await service.getReturnSettings('FRA');
   * console.log(settings.allow_return_creation_in_shop);
   *
   * @example
   * // Get default settings
   * const defaultSettings = await service.getReturnSettings();
   */
  async getReturnSettings(countryCode) {
    const params = {
      default_settings: true,
    }
    if (countryCode) {
      params.countries = [countryCode]
    }

    const queryParams = {
      filters: JSON.stringify(params),
    }

    const response = await this.get('/v1/int/settings/', queryParams)

    if (response.data) {
      // Try to find settings for the specified country
      let settings = response.data.find((item) => item.country_code === countryCode)

      // Fallback to default settings if country-specific settings not found
      if (!settings) {
        settings = response.data.find((item) => item.default_settings === true)
      }

      return settings || null
    }

    return null
  }
}
